/**
 * Data API client using openapi-fetch and openapi-react-query
 */

import createClient from 'openapi-fetch'
import type { paths } from './generated/schema'

// Type-safe client for the Data API
export type DataApiClient = ReturnType<typeof createClient<paths>>

/**
 * Configuration options for the Data API client
 */
export interface DataApiConfig {
  baseUrl?: string
  headers?: HeadersInit
  credentials?: RequestCredentials
}

/**
 * Get default configuration for the Data API
 */
function getDefaultConfig(): Required<DataApiConfig> {
  // Check environment variables first, then fallback to defaults based on NODE_ENV
  const baseUrl = 
    process.env.NEXT_PUBLIC_DATA_API_URL || 
    process.env.DATA_API_URL || 
    'https://data-api.dev.aminochain.io'
    // (process.env.NODE_ENV === 'production' 
    //   ? 'https://data-api.aminochain.io/'
    //   : 'https://data-api.dev.aminochain.io')

  // Log warning if no env vars are set (only in development)
  if (process.env.NODE_ENV !== 'production' && !process.env.NEXT_PUBLIC_DATA_API_URL && !process.env.DATA_API_URL) {
    console.warn('DATA_API_URL not configured - using default:', baseUrl)
  }

  return {
    baseUrl,
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin',
  }
}

/**
 * Create a configured Data API client instance
 */
export function createDataApiClient(config: DataApiConfig = {}): DataApiClient {
  const mergedConfig = { ...getDefaultConfig(), ...config }
  
  const client = createClient<paths>({
    baseUrl: mergedConfig.baseUrl,
    headers: mergedConfig.headers,
    credentials: mergedConfig.credentials,
  })

  // Add request interceptors for authentication
  client.use({
    async onRequest({ request }) {
      // Add auth token if available (from localStorage, cookies, etc.)
      const token = getAuthToken()
      if (token) {
        request.headers.set('Authorization', `Bearer ${token}`)
      }
      return request
    },

    async onResponse({ response }) {
      // Handle global response errors
      if (!response.ok) {
        console.error('API request failed:', {
          status: response.status,
          statusText: response.statusText,
          url: response.url,
        })
      }
      return response
    }
  })

  return client
}

/**
 * Get authentication token (implement based on your auth strategy)
 */
function getAuthToken(): string | null {
  // TODO: Implement based on your authentication system
  // This could be from localStorage, cookies, or a token store
  if (typeof window !== 'undefined') {
    return localStorage.getItem('auth-token')
  }
  return null
}

/**
 * Set authentication token
 */
export function setAuthToken(token: string): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem('auth-token', token)
  }
}

/**
 * Clear authentication token
 */
export function clearAuthToken(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('auth-token')
  }
}

// Create a default client instance
export const dataApiClient = createDataApiClient()

// Export type-safe paths and components
export type { paths } from './generated/schema'
export type { components } from './generated/schema'